import { ref } from 'vue'

import { useTwBenchmarkTagsStore } from '../store/useTwBenchmarkTagsStore'

export const MAX_SELECTABLE_POST_COUNT = 100

/**
 * タグ付与（単体・一括）の UI ロジック composable。
 */
export function useTagAssignment() {
  const tagsStore = useTwBenchmarkTagsStore()

  const selectedPostIds = ref<number[]>([])

  const toggleSelect = (postId: number): boolean => {
    const index = selectedPostIds.value.indexOf(postId)
    if (index === -1) {
      if (selectedPostIds.value.length >= MAX_SELECTABLE_POST_COUNT) {
        return false
      }
      selectedPostIds.value = [...selectedPostIds.value, postId]
    } else {
      selectedPostIds.value = selectedPostIds.value.filter(id => id !== postId)
    }
    return true
  }

  const isSelected = (postId: number): boolean => {
    return selectedPostIds.value.includes(postId)
  }

  const toggleSelectAll = (postIds: number[]): boolean => {
    const isPageAllSelected = postIds.length > 0 && postIds.every(id => selectedPostIds.value.includes(id))
    if (isPageAllSelected) {
      selectedPostIds.value = selectedPostIds.value.filter(id => !postIds.includes(id))
      return true
    }

    const additions = postIds.filter(id => !selectedPostIds.value.includes(id))
    const capacity = MAX_SELECTABLE_POST_COUNT - selectedPostIds.value.length
    selectedPostIds.value = [...selectedPostIds.value, ...additions.slice(0, capacity)]

    return additions.length <= capacity
  }

  const clearSelection = (): void => {
    selectedPostIds.value = []
  }

  const assignSingle = async (postId: number, tagIds: number[]): Promise<boolean> => {
    return tagsStore.overwritePostTags(postId, tagIds)
  }

  const assignBulk = async (tagIds: number[]): Promise<boolean> => {
    const assignments = selectedPostIds.value.map(postId => ({ postId, tagIds }))
    const isSuccess = await tagsStore.bulkAssignTags(assignments)

    if (isSuccess) {
      clearSelection()
    }

    return isSuccess
  }

  const unassign = async (postId: number, tagIds: number[]): Promise<boolean> => {
    return tagsStore.unassignPostTags(postId, tagIds)
  }

  return {
    selectedPostIds,
    toggleSelect,
    toggleSelectAll,
    isSelected,
    clearSelection,
    assignSingle,
    assignBulk,
    unassign
  }
}
